/**
 * CodeMirror 6 syntax-highlight themes for the markdown source view
 * (EDITOR-7602). Styling is tag-based: `@lezer/markdown` tags its nodes and
 * `@codemirror/lang-markdown` nests embedded grammars (fenced code, raw HTML,
 * etc.), so we only map `@lezer/highlight` tags → styles here — no language is
 * treated specially.
 *
 * The default palette is Bitbucket-inspired (red headings + syntax punctuation,
 * blue code), built from ADS tokens which adapt to the active colour mode.
 * Themes are produced via `getMarkdownHighlightStyle(theme, mode)` with explicit
 * light/dark palettes — the seam for a future theme picker.
 */
import { HighlightStyle } from '@codemirror/language';
import { tags } from '@lezer/highlight';

import { token } from '@atlaskit/tokens';

export type MarkdownSyntaxThemeName = 'default';

export type MarkdownSyntaxColorMode = 'light' | 'dark';

/** Semantic colour roles a theme maps onto; each role drives several tags. */
export type MarkdownSyntaxPalette = {
	code: string;
	comment: string;
	heading: string;
	keyword: string;
	link: string;
	literal: string;
	name: string;
	punctuation: string;
	string: string;
	text: string;
	type: string;
};

// ADS accent tokens (which adapt to colour mode); the per-mode hexes are
// fallbacks. Kept as two definitions so either can diverge later.
const DEFAULT_PALETTES: Record<MarkdownSyntaxColorMode, MarkdownSyntaxPalette> = {
	light: {
		heading: token('color.text.accent.red', '#AE2A19'),
		punctuation: token('color.text.accent.red', '#AE2A19'),
		code: token('color.text.accent.blue', '#0C66E4'),
		keyword: token('color.text.accent.purple', '#5E4DB2'),
		string: token('color.text.accent.green', '#216E4E'),
		literal: token('color.text.accent.teal', '#206A83'),
		type: token('color.text.accent.blue', '#0C66E4'),
		name: token('color.text.accent.purple', '#5E4DB2'),
		link: token('color.link', '#0C66E4'),
		comment: token('color.text.subtlest', '#626F86'),
		text: token('color.text', '#172B4D'),
	},
	dark: {
		heading: token('color.text.accent.red', '#FD9891'),
		punctuation: token('color.text.accent.red', '#FD9891'),
		code: token('color.text.accent.blue', '#579DFF'),
		keyword: token('color.text.accent.purple', '#9F8FEF'),
		string: token('color.text.accent.green', '#7EE2B8'),
		literal: token('color.text.accent.teal', '#9DD9EE'),
		type: token('color.text.accent.blue', '#579DFF'),
		name: token('color.text.accent.purple', '#9F8FEF'),
		link: token('color.link', '#579DFF'),
		comment: token('color.text.subtlest', '#8C9BAB'),
		text: token('color.text', '#C7D1DB'),
	},
};

const THEME_PALETTES: Record<
	MarkdownSyntaxThemeName,
	Record<MarkdownSyntaxColorMode, MarkdownSyntaxPalette>
> = {
	default: DEFAULT_PALETTES,
};

function buildHighlightStyle(p: MarkdownSyntaxPalette): HighlightStyle {
	return HighlightStyle.define([
		{
			tag: tags.heading,
			color: p.heading,
			fontWeight: token('font.weight.bold'),
		},
		// Every syntax mark (`#`, `**`, `` ` ``, `>`, list/table marks) shares
		// `processingInstruction`; `tags.list` is left unstyled so item text
		// stays at the default foreground while the marker is coloured.
		{ tag: tags.processingInstruction, color: p.punctuation },
		{ tag: tags.contentSeparator, color: p.punctuation },
		{ tag: tags.emphasis, fontStyle: 'italic' },
		{
			tag: tags.strong,
			fontWeight: token('font.weight.bold'),
		},
		{ tag: tags.strikethrough, textDecoration: 'line-through' },
		{ tag: [tags.link, tags.url], color: p.link, textDecoration: 'underline' },
		{ tag: tags.monospace, color: p.code },
		{ tag: [tags.angleBracket, tags.tagName], color: p.punctuation },
		{ tag: tags.attributeName, color: p.name },
		{ tag: tags.attributeValue, color: p.string },
		{ tag: tags.comment, color: p.comment, fontStyle: 'italic' },
		{
			tag: tags.keyword,
			color: p.keyword,
			fontWeight: token('font.weight.bold'),
		},
		{ tag: [tags.string, tags.special(tags.string), tags.inserted], color: p.string },
		{ tag: [tags.atom, tags.bool, tags.literal, tags.labelName, tags.number], color: p.literal },
		{ tag: [tags.typeName, tags.namespace], color: p.type },
		{ tag: [tags.className, tags.propertyName], color: p.name },
		{ tag: tags.deleted, color: p.punctuation },
		{ tag: [tags.regexp, tags.escape], color: p.literal },
		{ tag: tags.variableName, color: p.text },
		{ tag: tags.invalid, color: p.text },
	]);
}

const themeCache = new Map<string, HighlightStyle>();

/** Memoised `HighlightStyle` for a theme + colour mode. */
export function getMarkdownHighlightStyle(
	theme: MarkdownSyntaxThemeName = 'default',
	mode: MarkdownSyntaxColorMode = 'light',
): HighlightStyle {
	const key = `${theme}:${mode}`;
	const cached = themeCache.get(key);
	if (cached) {
		return cached;
	}
	const palettes = THEME_PALETTES[theme] ?? THEME_PALETTES.default;
	const style = buildHighlightStyle(palettes[mode] ?? palettes.light);
	themeCache.set(key, style);
	return style;
}

/** Default style — `getMarkdownHighlightStyle('default', 'light')`. */
export const markdownHighlightStyle: HighlightStyle = getMarkdownHighlightStyle('default', 'light');
